// src/components/JourneyMap.js
// Metro-style 28-station journey map.
// Used in two spots:
//   1. Dashboard Journey tab — full map above the rolodex
//   2. StationEntry pre-journal — overlay before Begin, tap station to confirm

import React, { useRef, useEffect, useCallback, useState } from 'react'
import { CITIES, DAYS } from '../data/content'

/* ── City-specific config ── */
const CITY_CONFIG = {
  nyc: {
    labelAngle: -90,       // straight up — MTA utilitarian
    lineStyle: 'square',   // square stroke caps
    gridLines: true,       // subtle background grid
    badge: (c) => <div style={{ width:44, height:44, borderRadius:'50%', background:c.primaryColor, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:'1.3rem', fontFamily:'var(--fn)', boxShadow:'inset 0 -3px 0 rgba(0,0,0,0.2)' }}>1</div>,
  },
  paris: {
    labelAngle: -48,       // diagonal — real RATP style
    lineStyle: 'round',
    gridLines: false,
    badge: (c) => (
      <div style={{ display:'flex', gap:6 }}>
        <div style={{ width:40, height:40, borderRadius:'50%', border:`3px solid ${c.primaryColor}`, color:c.primaryColor, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontFamily:'var(--fn)', fontSize:'0.9rem' }}>M</div>
        <div style={{ width:40, height:40, borderRadius:'50%', background:'#C04191', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:'1.1rem', fontFamily:'var(--fn)' }}>4</div>
      </div>
    ),
  },
  london: {
    labelAngle: -45,
    lineStyle: 'round',
    gridLines: false,
    badge: (c) => <div style={{ width:44, height:44, borderRadius:'50%', background:c.primaryColor, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:'1.3rem', fontFamily:'var(--fn)' }}>U</div>,
  },
  milan: {
    labelAngle: -90,
    lineStyle: 'square',
    gridLines: false,
    badge: (c) => <div style={{ width:44, height:44, borderRadius:'50%', background:c.primaryColor, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:'1.1rem', fontFamily:'var(--fn)' }}>M</div>,
  },
  berlin: {
    labelAngle: -90,
    lineStyle: 'square',
    gridLines: true,
    badge: (c) => <div style={{ width:44, height:44, borderRadius:'50%', background:c.primaryColor, color:'#111', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:'1.2rem', fontFamily:'var(--fn)' }}>U</div>,
  },
  madrid: {
    labelAngle: -48,
    lineStyle: 'round',
    gridLines: false,
    badge: (c) => <div style={{ width:44, height:44, borderRadius:'50%', background:c.primaryColor, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:'1.1rem', fontFamily:'var(--fn)' }}>M</div>,
  },
}

const MILESTONES = [7, 14, 21, 28]
const STEP = 84   // px between stations
const LINE_Y = 16 // baseline Y within the SVG viewBox

/* ── Helper: SVG element factory ── */
function mkEl(tag) {
  return document.createElementNS('http://www.w3.org/2000/svg', tag)
}
function setAttrs(el, attrs) {
  const map = {
    strokeWidth: 'stroke-width', strokeLinecap: 'stroke-linecap',
    strokeDasharray: 'stroke-dasharray', strokeDashoffset: 'stroke-dashoffset',
    textAnchor: 'text-anchor', dominantBaseline: 'dominant-baseline',
    fontSize: 'font-size', fontWeight: 'font-weight', fontFamily: 'font-family',
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === 'class') el.setAttribute('class', v)
    else if (k === 'style') el.setAttribute('style', v)
    else el.setAttribute(map[k] || k, v)
  })
  return el
}

/* ── Main component ── */
export default function JourneyMap({
  city,
  cityProgress = 0,       // number of completed stations (0-based count)
  onStationPress,         // called with stationNum when user taps a station
  mode = 'dashboard',     // 'dashboard' | 'prejournal'
  style = {},
}) {
  const svgRef       = useRef(null)
  const scrollRef    = useRef(null)
  const [tooltip, setTooltip] = useState(null) // { x, y, num, day, station }
  const [hovered, setHovered] = useState(null)

  const cityData   = CITIES[city] || CITIES.nyc
  const days       = (DAYS || {})[city] || []
  const cfg        = CITY_CONFIG[city] || CITY_CONFIG.nyc
  const primary    = city === 'berlin' ? '#b89800' : cityData.primaryColor
  const unit       = cityData.unitLabel || 'Station'
  const currentStation = Math.min(cityProgress + 1, 28) // 1-based next station

  /* ── Draw the SVG map ── */
  const draw = useCallback(() => {
    const svg = svgRef.current
    if (!svg) return

    const n   = 28
    const W   = STEP * (n - 1) + 100
    const H   = 52  // SVG viewport height — labels overflow via overflow:visible

    svg.setAttribute('width', W)
    svg.setAttribute('height', H)
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`)
    svg.innerHTML = ''

    const lY = LINE_Y

    // ── Background line ──
    const bgLine = mkEl('line')
    setAttrs(bgLine, {
      x1: 20, y1: lY, x2: 20 + STEP * (n - 1), y2: lY,
      stroke: `${primary}20`, strokeWidth: 5,
      strokeLinecap: cfg.lineStyle,
    })
    svg.appendChild(bgLine)

    // ── Completed line (animated) ──
    const doneX = 20 + STEP * (currentStation - 1)
    const doneLine = mkEl('line')
    setAttrs(doneLine, {
      x1: 20, y1: lY, x2: doneX, y2: lY,
      stroke: primary, strokeWidth: 5,
      strokeLinecap: cfg.lineStyle,
      class: 'jm-line-draw',
    })
    svg.appendChild(doneLine)

    // ── Stations ──
    days.forEach((day, i) => {
      const x        = 20 + STEP * i
      const num      = i + 1
      const isDone   = num < currentStation
      const isCur    = num === currentStation
      const isFuture = num > currentStation
      const isMile   = MILESTONES.includes(num)
      const isEnd    = num === 1 || num === 28
      const dayColor = day.color || primary
      const station  = day.stationName || '' // may be empty

      const g = mkEl('g')
      g.setAttribute('class', `jm-stop${isCur ? ' jm-stop-current' : ''}`)
      g.setAttribute('data-num', num)
      g.style.cursor = 'pointer'

      // ── Dot ──
      if (isCur) {
        // Pulse glow
        const glow = mkEl('circle')
        setAttrs(glow, { cx: x, cy: lY, r: 18, fill: `${primary}15`, stroke: 'none' })
        g.appendChild(glow)
        const ring = mkEl('circle')
        setAttrs(ring, { cx: x, cy: lY, r: 13, fill: 'none', stroke: primary, strokeWidth: 1.5, opacity: 0.7 })
        g.appendChild(ring)
        const dot = mkEl('circle')
        setAttrs(dot, { cx: x, cy: lY, r: 9, fill: primary, stroke: '#fff', strokeWidth: 2.5, class: 'jm-pulse' })
        g.appendChild(dot)

      } else if (isMile) {
        // Diamond
        const s = 9
        const d = mkEl('polygon')
        d.setAttribute('points', `${x},${lY - s} ${x + s},${lY} ${x},${lY + s} ${x - s},${lY}`)
        setAttrs(d, {
          fill:   isDone ? dayColor : 'var(--bg2, #111)',
          stroke: isDone ? dayColor : `${primary}66`,
          strokeWidth: '2',
          class: 'jm-dot-in',
          style: `animation-delay:${i * 0.025}s`,
        })
        g.appendChild(d)

      } else if (isEnd && city !== 'paris') {
        // Square terminus (MTA style for NYC, Berlin)
        const sq = mkEl('rect')
        setAttrs(sq, {
          x: x - 5, y: lY - 5, width: 10, height: 10,
          fill:   isDone ? dayColor : 'var(--bg2, #111)',
          stroke: isDone ? dayColor : `${primary}44`,
          strokeWidth: '2',
          class: 'jm-dot-in',
        })
        g.appendChild(sq)

      } else {
        const dot = mkEl('circle')
        setAttrs(dot, {
          cx: x, cy: lY, r: 6,
          fill:   isDone ? dayColor : 'var(--bg2, #111)',
          stroke: isDone ? dayColor : `${primary}44`,
          strokeWidth: '2',
          class: 'jm-dot-in',
          style: `animation-delay:${i * 0.025}s`,
        })
        g.appendChild(dot)
      }

      // ── Vertical tick from dot up to label ──
      const tickEl = mkEl('line')
      setAttrs(tickEl, {
        x1: x, y1: lY - 8, x2: x, y2: lY - 16,
        stroke: isFuture ? `${primary}1A` : `${primary}44`,
        strokeWidth: 1,
      })
      svg.appendChild(tickEl)

      // ── Primary label — day title ──
      const labelY = lY - 18
      const t1 = mkEl('text')
      setAttrs(t1, {
        x, y: labelY,
        transform: `rotate(${cfg.labelAngle}, ${x}, ${labelY})`,
        textAnchor: 'start',
        dominantBaseline: 'central',
        fontFamily: 'var(--fn, HelvNow, Helvetica Neue, sans-serif)',
        fontSize: isMile ? '12.5' : '11.5',
        fontWeight: isCur || isMile ? '900' : '700',
        fill: isFuture ? `${primary}33`
              : isCur    ? primary
              : 'var(--text, #fff)',
        opacity: isDone ? '0.75' : '1',
      })
      t1.textContent = day.title || `Day ${num}`
      svg.appendChild(t1)

      // ── Secondary label — real station name ──
      if (day.stationName) {
        const t2 = mkEl('text')
        setAttrs(t2, {
          x, y: labelY,
          transform: `rotate(${cfg.labelAngle}, ${x}, ${labelY})`,
          textAnchor: 'start',
          dominantBaseline: 'central',
          fontFamily: 'var(--fn, HelvNow, Helvetica Neue, sans-serif)',
          fontSize: '9',
          fontWeight: '700',
          fill: isFuture ? `${primary}1A`
                : isCur    ? `${primary}88`
                : `${primary}55`,
          dy: '13',
        })
        t2.textContent = day.stationName
        svg.appendChild(t2)
      }

      // ── Station number ──
      const tNum = mkEl('text')
      setAttrs(tNum, {
        x, y: labelY,
        transform: `rotate(${cfg.labelAngle}, ${x}, ${labelY})`,
        textAnchor: 'end',
        dominantBaseline: 'central',
        fontFamily: 'var(--fn, HelvNow, Helvetica Neue, sans-serif)',
        fontSize: '9',
        fontWeight: '900',
        fill: isCur    ? primary
              : isFuture ? `${primary}22`
              : `${primary}55`,
        dx: '-4',
      })
      tNum.textContent = num
      svg.appendChild(tNum)

      // ── Terminus box (filled rect label for first/last) ──
      if (isEnd) {
        const label = num === 1 ? 'Start' : 'Complete'
        const side  = num === 1 ? 'right' : 'left'
        const boxW  = label.length * 6.5 + 14
        const boxH  = 20
        const bx    = side === 'right' ? x + 13 : x - 13 - boxW
        const br    = city === 'nyc' || city === 'berlin' ? 0 : 3

        const rect = mkEl('rect')
        setAttrs(rect, {
          x: bx, y: lY - boxH / 2, width: boxW, height: boxH,
          rx: br,
          fill: isDone || isCur ? primary : `${primary}55`,
        })
        svg.appendChild(rect)

        const tBox = mkEl('text')
        setAttrs(tBox, {
          x: bx + 7, y: lY,
          dominantBaseline: 'central',
          fontFamily: 'var(--fn, HelvNow, Helvetica Neue, sans-serif)',
          fontSize: '9.5', fontWeight: '900', fill: num === 28 && city === 'berlin' ? '#111' : '#fff',
        })
        tBox.textContent = label
        svg.appendChild(tBox)
      }

      svg.appendChild(g)

      // ── Events ──
      g.addEventListener('mouseenter', (e) => {
        setHovered(num)
        setTooltip({
          x: e.clientX, y: e.clientY,
          num, title: day.title, station: day.stationName,
          isDone, isCur,
        })
      })
      g.addEventListener('mousemove', (e) => {
        setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)
      })
      g.addEventListener('mouseleave', () => {
        setHovered(null)
        setTooltip(null)
      })
      g.addEventListener('click', () => {
        if (onStationPress) onStationPress(num)
      })
    })

  }, [city, currentStation, primary, cfg, days, onStationPress])

  // ── Redraw on data change ──
  useEffect(() => { draw() }, [draw])

  // ── Scroll to current station ──
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const x = 20 + STEP * (currentStation - 1)
    setTimeout(() => {
      el.scrollTo({ left: Math.max(0, x - el.clientWidth / 2), behavior: 'smooth' })
    }, 300)
  }, [currentStation])

  // ── Drag-to-scroll ──
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let dragging = false, startX = 0, scrollLeft = 0
    const onDown  = e => { dragging = true; startX = e.pageX - el.offsetLeft; scrollLeft = el.scrollLeft; el.style.cursor = 'grabbing' }
    const onMove  = e => { if (!dragging) return; e.preventDefault(); el.scrollLeft = scrollLeft - (e.pageX - el.offsetLeft - startX) }
    const onUp    = ()  => { dragging = false; el.style.cursor = 'grab' }
    el.addEventListener('mousedown', onDown)
    el.addEventListener('mousemove', onMove)
    el.addEventListener('mouseup', onUp)
    el.addEventListener('mouseleave', onUp)
    return () => {
      el.removeEventListener('mousedown', onDown)
      el.removeEventListener('mousemove', onMove)
      el.removeEventListener('mouseup', onUp)
      el.removeEventListener('mouseleave', onUp)
    }
  }, [])

  const pct = ((currentStation - 1) / 27) * 100

  return (
    <div className="jm-wrap" style={style}>

      {/* ── CSS animations — injected once ── */}
      <style>{`
        @keyframes jm-draw {
          from { stroke-dashoffset: 4000; }
          to   { stroke-dashoffset: 0; }
        }
        @keyframes jm-fadeup {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes jm-pulse {
          0%, 100% { r: 9; }
          50%       { r: 13; }
        }
        .jm-line-draw {
          stroke-dasharray: 4000;
          stroke-dashoffset: 4000;
          animation: jm-draw 1.6s cubic-bezier(.4,0,.2,1) .1s forwards;
        }
        .jm-dot-in {
          animation: jm-fadeup .25s ease forwards;
          transform-box: fill-box;
          transform-origin: center;
        }
        .jm-pulse { animation: jm-pulse 2s ease-in-out infinite; }
        .jm-stop { transition: opacity .15s; }
        .jm-stop:hover { opacity: .85; }
      `}</style>

      {/* ── Header ── */}
      <div className="jm-header">
        <div className="jm-header-left">
          {cfg.badge(cityData)}
          <div className="jm-header-text">
            <div className="jm-city-name">{cityData.name}</div>
            <div className="jm-city-sub">28 {unit}s · The Magic Journey</div>
          </div>
        </div>
        <div className="jm-progress">
          <div className="jm-progress-frac">{currentStation - 1} <span>/28</span></div>
          <div className="jm-progress-bar">
            <div className="jm-progress-fill" style={{ width: `${pct}%`, background: primary }} />
          </div>
        </div>
      </div>

      {/* ── Scrollable map ── */}
      <div
        ref={scrollRef}
        className="jm-scroll"
        style={{ cursor: 'grab' }}
      >
        <svg
          ref={svgRef}
          className="jm-svg"
          xmlns="http://www.w3.org/2000/svg"
          style={{ overflow: 'visible', display: 'block' }}
        />
      </div>

      {/* ── Current station callout ── */}
      {days[currentStation - 1] && (
        <div
          className="jm-callout"
          style={{ background: primary, borderLeft: city === 'nyc' || city === 'berlin' ? '4px solid #FCCC0A' : 'none', borderRadius: city === 'nyc' || city === 'berlin' ? 0 : 10 }}
          onClick={() => onStationPress && onStationPress(currentStation)}
        >
          <div className="jm-callout-dot" style={{ background: 'rgba(255,255,255,0.25)', color: '#fff' }}>
            {currentStation}
          </div>
          <div className="jm-callout-body">
            <div className="jm-callout-title">{days[currentStation - 1].title}</div>
            <div className="jm-callout-sub">
              {unit} {currentStation}
              {mode === 'prejournal' ? ' · Tap to begin today' : ' · Your current stop'}
            </div>
          </div>
          {mode === 'prejournal' && (
            <div className="jm-callout-arrow" style={{ color: '#FCCC0A' }}>→</div>
          )}
        </div>
      )}

      {/* ── Tooltip ── */}
      {tooltip && (
        <div
          className="jm-tooltip"
          style={{
            position: 'fixed',
            left: tooltip.x + 16,
            top:  tooltip.y - 10,
            background: primary,
            color: '#fff',
            padding: '7px 12px',
            borderRadius: city === 'nyc' ? 0 : 6,
            fontSize: '0.72rem',
            fontWeight: 600,
            pointerEvents: 'none',
            zIndex: 1000,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            borderLeft: '3px solid rgba(255,255,255,0.4)',
            fontFamily: 'var(--fn)',
          }}
        >
          <div style={{ fontWeight: 900 }}>{tooltip.num}. {tooltip.title}</div>
          {tooltip.station && <div style={{ opacity: 0.6, fontSize: '0.65rem', marginTop: 2 }}>{tooltip.station}</div>}
          <div style={{ opacity: 0.5, fontSize: '0.6rem', marginTop: 2, letterSpacing: '1px', textTransform: 'uppercase' }}>
            {tooltip.isCur ? 'Current Stop ✦' : tooltip.isDone ? 'Completed' : 'Upcoming'}
          </div>
        </div>
      )}
    </div>
  )
}
